import React from "react";

// Component imports
import { DialogButton } from "@components";
import { Grid, Paper, Typography } from "@material";

// Misc Imports
import { useTranslation } from "@i18n";
import { BTCFiatAmount, WalletTransaction, WalletTransactionStatus } from "@models";
import { makeStyles } from "@styles";
import { ModalHelper } from "@utils";

type DetailRow = {
  label: string;
  value: string;
};

type Props = {
  transaction: WalletTransaction;
  transactionDetails: ITransactionDetails;
  createdAt: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: 480,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    padding: 0,
    overflow: "hidden",
    borderRadius: 8,
    outline: "none",
  },
  content: {
    maxHeight: "calc(100vh - 128px)",
    overflowY: "auto",
    padding: "32px 32px 12px",
    [theme.breakpoints.down("xs")]: {
      padding: "24px 20px 8px",
    },
  },
  imageContainer: {
    width: 112,
    height: 112,
    marginBottom: 18,
    borderRadius: "50%",
    border: "1px solid #EBEBEB",
    backgroundColor: "#FAFAFA",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    overflow: "hidden",
  },
  fallbackInitial: {
    fontSize: 40,
    fontWeight: 700,
  },
  title: {
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.2,
    textAlign: "center",
  },
  subtitle: {
    marginTop: 8,
    paddingLeft: 8,
    paddingRight: 8,
    color: "#606060",
    fontSize: 16,
    lineHeight: 1.35,
    textAlign: "center",
  },
  rows: {
    width: "100%",
    marginTop: 28,
    borderTop: "1px solid #EBEBEB",
  },
  row: {
    minHeight: 48,
    paddingTop: 14,
    paddingBottom: 14,
    borderBottom: "1px solid #EBEBEB",
  },
  label: {
    color: "#606060",
    fontSize: 14,
  },
  value: {
    color: "#393939",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
  buttonContainer: {
    padding: "12px 32px 28px",
    [theme.breakpoints.down("xs")]: {
      padding: "12px 20px 24px",
    },
  },
}));

function getTransactionData(transaction: WalletTransaction): any {
  return transaction?.externalRef?.data || {};
}

function formatAmount(transaction: WalletTransaction): string {
  if (!transaction?.amount) return "---";

  const { fiat } = new BTCFiatAmount(transaction.amount);
  if (!fiat?.currency || fiat?.unscaledValue === undefined) return "---";

  return `${fiat.currency} $${fiat.unscaledValue.toFixed(2)}`;
}

function getPurchaseAmount(data: any): string {
  const totalSaleAmount =
    typeof data?.totalSaleAmount === "number"
      ? data.totalSaleAmount
      : typeof data?.totalSaleAmount === "string"
      ? Number(data.totalSaleAmount)
      : null;

  if (typeof totalSaleAmount === "number" && !Number.isNaN(totalSaleAmount)) {
    const currency = data?.currency || "CAD";
    return `${currency} $${totalSaleAmount.toFixed(2)}`;
  }

  if (data?.merchantValue) return String(data.merchantValue);

  if (typeof data?.spentAmount === "number") {
    return `CAD $${(data.spentAmount / 100).toFixed(2)}`;
  }

  return "---";
}

function buildRows(
  transaction: WalletTransaction,
  transactionDetails: ITransactionDetails,
  createdAt: string,
  t: (key: string) => string,
): DetailRow[] {
  const rows: DetailRow[] = [];
  const data = getTransactionData(transaction);
  const type = transaction?.externalRef?.type;
  const status = transaction?.status;

  if ((type === "GiftCard" || type === "GiftCardAirdrop") && data?.giftDetails?.name) {
    rows.push({ label: t("transactionDetails.recipient"), value: data.giftDetails.name });
  }

  if (
    (type === "GiftCard" || type === "GiftCardAirdrop") &&
    data?.giftDetails?.emailAddress
  ) {
    rows.push({
      label: t("transactionDetails.recipientEmail"),
      value: data.giftDetails.emailAddress,
    });
  }

  if (type === "GiftCard" || type === "GiftCardAirdrop") {
    rows.push({
      label:
        type === "GiftCardAirdrop"
          ? t("transactionDetails.yourAirdrop")
          : t("transactionDetails.yourGiftCardPurchase"),
      value: getPurchaseAmount(data),
    });
  } else if (type === "Affiliate" || type === "Travel") {
    if (type === "Travel") {
      rows.push({
        label: t("transactionDetails.booking"),
        value: data?.hotelName || transactionDetails.itemTitle,
      });
    }

    rows.push({
      label:
        type === "Travel"
          ? t("transactionDetails.bookingAmount")
          : t("transactionDetails.yourPurchase"),
      value: getPurchaseAmount(data),
    });
  } else if (type === "Interac") {
    rows.push({
      label: t("transactionDetails.sentViaInterac"),
      value: data?.emailAddress || data?.recipientEmail || "---",
    });
  } else {
    rows.push({
      label: t("transactionDetails.amount"),
      value: formatAmount(transaction),
    });
  }

  rows.push({ label: t("transactionDetails.date"), value: createdAt });

  if (
    transactionDetails.fiatValue &&
    status !== WalletTransactionStatus.MissingTransaction
  ) {
    rows.push({
      label: t("transactionDetails.reward"),
      value: transactionDetails.fiatValue,
    });
  }

  if (
    status === WalletTransactionStatus.MissingTransaction ||
    status === "Frozen" ||
    status === "PendingBalanceCheck" ||
    status === "PendingExternalTransfert" ||
    status === "Reversed"
  ) {
    rows.push({
      label: t("transactionDetails.status"),
      value:
        status === "Reversed"
          ? t("transactionDetails.statusReversed")
          : t("transactionDetails.statusPending"),
    });
  }

  if (data?.lockingDate && status !== "Completed") {
    rows.push({
      label: t("transactionDetails.fundsAvailableAfter"),
      value: new Date(data.lockingDate).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      } as any),
    });
  }

  return rows;
}

export function WalletTransactionDetailModal({
  transaction,
  transactionDetails,
  createdAt,
}: Props) {
  const classes: any = useStyles({});
  const { t } = useTranslation(["wallet", "common"]);
  const rows = buildRows(transaction, transactionDetails, createdAt, (key) =>
    t(`wallet:${key}`),
  );
  const initial = transactionDetails.itemTitle?.trim()?.charAt(0)?.toUpperCase() || "?";
  const isReversed = transaction?.status === "Reversed";
  const subtitle = isReversed
    ? t("wallet:transactionDetails.reversedSubtitle")
    : transaction?.status === WalletTransactionStatus.MissingTransaction
    ? t("wallet:transactionDetails.pendingSubtitle")
    : transactionDetails.fiatValue
    ? t("wallet:transactionDetails.earnedSubtitle", {
        reward: transactionDetails.fiatValue,
      })
    : "";

  return (
    <Paper className={classes.paper}>
      <Grid container direction="column" alignItems="center" className={classes.content}>
        <Grid
          container
          item
          alignItems="center"
          justifyContent="center"
          className={classes.imageContainer}
          style={{
            backgroundImage: transactionDetails.imageSource
              ? `url("${transactionDetails.imageSource}")`
              : "none",
          }}
        >
          {!transactionDetails.imageSource && (
            <Typography className={classes.fallbackInitial}>{initial}</Typography>
          )}
        </Grid>
        <Typography className={classes.title}>{transactionDetails.itemTitle}</Typography>
        {!!subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}

        <Grid container item direction="column" className={classes.rows}>
          {rows.map((row) => (
            <Grid
              container
              item
              key={row.label}
              alignItems="flex-start"
              justifyContent="space-between"
              wrap="nowrap"
              className={classes.row}
            >
              <Grid item xs={5}>
                <Typography className={classes.label}>{row.label}</Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography className={classes.value}>{row.value}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid className={classes.buttonContainer}>
        <DialogButton colorVariant="black" width="100%" onClick={ModalHelper.close}>
          {t("common:gotIt")}
        </DialogButton>
      </Grid>
    </Paper>
  );
}
